import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogContent, DialogActions, DialogTitle,
    Button, Grid, Typography, Box, CircularProgress,
    Table, TableBody, TableCell, TableHead, TableRow,
    TextField, MenuItem, Switch, IconButton, Tabs, Tab,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';

import {
    listFeaturesService,
    getInsiderFeatureAccessService,
    setInsiderFeatureOverridesService,
    removeInsiderFeatureOverrideService,
    resetInsiderFeatureOverridesService,
} from "../../../../services/feature.service";
import { toastSuccess, toastWarning } from "../../../../utils/utils";
import LoaderWithBackDrop from "../../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import BrandingPanel from '../../../Organizations/Licenses_Billing/components/BrandingPanel.comp';
import { FEATURE_KEY } from '../../../../utils/constants';

/**
 * Admin dialog to force-enable / disable platform features for one insider
 * (feature_access_overrides). Includes white-label branding on a second tab.
 */
const InsiderFeaturesDialog = ({ open, onClose, onClosed, dq_insider_id, workspaceName }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [brandingRefreshKey, setBrandingRefreshKey] = useState(0);

    const [catalog, setCatalog] = useState([]);
    const [overrides, setOverrides] = useState({});
    const [resolved, setResolved] = useState({});
    const [featureKey, setFeatureKey] = useState('');

    const loadData = async ({ silent = false } = {}) => {
        if (!dq_insider_id) return;
        if (!silent) setLoading(true);
        try {
            const [catRes, ovrRes] = await Promise.all([
                listFeaturesService(),
                getInsiderFeatureAccessService(dq_insider_id),
            ]);
            setCatalog((catRes.data && catRes.data.features) || []);
            setOverrides((ovrRes.data && ovrRes.data.overrides) || {});
            // Effective features the insider actually has (plan-active + overrides +
            // self-activated). Plan features live here, NOT in `overrides`.
            setResolved((ovrRes.data && ovrRes.data.resolved) || {});
        } catch (e) {
            toastWarning("Failed to load features.");
        }
        if (!silent) setLoading(false);
    };

    const refreshBrandingTab = () => setBrandingRefreshKey((k) => k + 1);

    const syncAfterBrandingAccessChange = () => loadData({ silent: true });

    const syncAfterFeatureMutation = (feature_key) => {
        loadData({ silent: true });
        if (feature_key === FEATURE_KEY.WHITE_LABEL_BRANDING) {
            refreshBrandingTab();
        }
    };

    useEffect(() => {
        if (open) {
            setActiveTab(0);
            setBrandingRefreshKey((k) => k + 1);
            loadData();
        } else {
            setCatalog([]);
            setOverrides({});
            setResolved({});
            setFeatureKey('');
            setActiveTab(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dq_insider_id]);

    const handleClose = () => {
        onClose();
        if (typeof onClosed === 'function') onClosed();
    };

    const labelFor = (key) => (catalog.find(c => c.feature_key === key) || {}).label || key;
    const overrideKeys = Object.keys(overrides || {});
    const addableFeatures = catalog.filter(c => !overrideKeys.includes(c.feature_key));

    // One toggleable list = plan-resolved-enabled features (e.g. Partner Dashboard on
    // enterprise_p0) UNION any manual overrides. Each row shows the effective enabled
    // state with a Switch so the admin can force-enable/disable a plan feature; a
    // reset action removes the override and reverts to the plan default.
    const _displayKeys = new Set([
        ...Object.keys(resolved || {}).filter(k => resolved[k] && resolved[k].enabled),
        ...overrideKeys,
    ]);
    // Deterministic row order (catalog order). Without this the rows are ordered
    // enabled-first (Set: resolved-enabled keys, then overrides), so toggling a
    // feature on/off makes it jump between the two groups and the rows reorder —
    // which looks like the wrong row got toggled. A fixed order keeps each feature
    // in the same place regardless of its enabled state.
    const _catalogOrder = (catalog || []).map((c) => c.feature_key);
    const _rowSortIndex = (k) => {
        const i = _catalogOrder.indexOf(k);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    const displayRows = Array.from(_displayKeys).map((key) => {
        const hasOverride = overrideKeys.includes(key);
        const enabled = hasOverride
            ? overrides[key]?.enabled !== false
            : !!(resolved[key] && resolved[key].enabled);
        const source = hasOverride ? 'admin_override' : (resolved[key]?.source || 'plan');
        return { key, enabled, source, hasOverride };
    }).sort((a, b) => (_rowSortIndex(a.key) - _rowSortIndex(b.key)) || a.key.localeCompare(b.key));

    const setOverride = async (feature_key, enabled) => {
        setSubmitting(true);
        try {
            await setInsiderFeatureOverridesService(dq_insider_id, { [feature_key]: { enabled } });
            toastSuccess("Feature override saved.");
            syncAfterFeatureMutation(feature_key);
        } catch (e) { toastWarning("Failed to save feature override."); }
        setSubmitting(false);
    };

    const addOverride = async () => {
        if (!featureKey) return toastWarning("Select a feature.");
        await setOverride(featureKey, true);
        setFeatureKey('');
    };

    const removeOverride = async (feature_key) => {
        setSubmitting(true);
        try {
            await removeInsiderFeatureOverrideService(dq_insider_id, feature_key);
            toastSuccess("Feature override removed.");
            syncAfterFeatureMutation(feature_key);
        } catch (e) { toastWarning("Failed to remove override."); }
        setSubmitting(false);
    };

    const resetAll = async () => {
        if (!window.confirm("Reset all feature overrides for this insider?")) return;
        setSubmitting(true);
        try {
            await resetInsiderFeatureOverridesService(dq_insider_id);
            toastSuccess("Feature overrides reset.");
            loadData({ silent: true });
            refreshBrandingTab();
        } catch (e) { toastWarning("Failed to reset overrides."); }
        setSubmitting(false);
    };

    const handleTabChange = (_, value) => {
        setActiveTab(value);
        if (value === 0) loadData({ silent: true });
        if (value === 1) refreshBrandingTab();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Manage Insider Features
                {workspaceName ? (
                    <Typography variant="caption" display="block" color="textSecondary">
                        {workspaceName}
                    </Typography>
                ) : null}
            </DialogTitle>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                style={{ borderBottom: '1px solid #e0e0e0', paddingLeft: 16 }}
            >
                <Tab label="Feature overrides" />
                <Tab label="Branding" />
            </Tabs>
            <DialogContent dividers style={{ minHeight: 320 }}>
                {activeTab === 0 && (
                    loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="textSecondary">
                                    Force-enable or disable platform features for this insider. Overrides take
                                    priority over the package&apos;s feature settings.
                                </Typography>
                                <Button size="small" color="secondary" onClick={resetAll} disabled={submitting || overrideKeys.length === 0}>
                                    Reset All
                                </Button>
                            </Box>

                            {/* One list = plan-resolved features (e.g. Partner Dashboard on
                                enterprise_p0) + manual overrides. Each row has a Switch so the
                                admin can force enable/disable; the reset (trash) removes the
                                override and reverts the feature to its plan default. */}
                            <Table size="small" style={{ marginTop: 12 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Feature</TableCell>
                                        <TableCell align="center">Enabled</TableCell>
                                        <TableCell align="right">Source</TableCell>
                                        <TableCell align="right">Reset</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayRows.map(({ key, enabled, source, hasOverride }) => (
                                        <TableRow key={key}>
                                            <TableCell>{labelFor(key)}<br /><small>{key}</small></TableCell>
                                            <TableCell align="center">
                                                <Switch
                                                    color="primary"
                                                    checked={enabled}
                                                    disabled={submitting}
                                                    onChange={(e) => setOverride(key, e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell align="right"><small>{source}</small></TableCell>
                                            <TableCell align="right">
                                                {hasOverride ? (
                                                    <IconButton size="small" title="Reset to plan default" onClick={() => removeOverride(key)}>
                                                        <DeleteIcon fontSize="small" color="error" />
                                                    </IconButton>
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {displayRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Typography color="textSecondary" variant="body2">No features enabled for this insider.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>

                            <Box mt={2}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={8}>
                                        <TextField select fullWidth size="small" label="Feature" variant="outlined"
                                                   value={featureKey} onChange={(e) => setFeatureKey(e.target.value)}>
                                            {addableFeatures.map(f => (
                                                <MenuItem key={f.feature_key} value={f.feature_key}>
                                                    {f.label} ({f.feature_key})
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Button variant="outlined" color="primary" fullWidth
                                                disabled={submitting || !featureKey} onClick={addOverride}>
                                            Add Override
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>
                        </>
                    )
                )}
                {activeTab === 1 && (
                    <BrandingPanel
                        active={open && activeTab === 1}
                        dqInsiderId={dq_insider_id}
                        refreshKey={brandingRefreshKey}
                        onAccessModeChanged={syncAfterBrandingAccessChange}
                    />
                )}
            </DialogContent>
            <DialogActions style={{ padding: '12px 24px' }}>
                <Button onClick={handleClose} color="primary" variant="contained" disableElevation>Close</Button>
            </DialogActions>
            <LoaderWithBackDrop loading={submitting} />
        </Dialog>
    );
};

export default InsiderFeaturesDialog;
